<template>
  <div class="services">
    <offers-header class="--margin-bottom"></offers-header>
    <card-flex class="services__message-alert"
               no-box-shadow
               closeMessage
               :content="$t('services.cardFlex.message')"
               :image="handShaking">
    </card-flex>
    <h3 class="services__mover --margin-top">{{ $t('services.mover.title') }}</h3>
    <offer-wrapper v-if="offer" :offer="offer" options-valided />
    <template v-if="servicesNxt?.length">
      <h3 class="services__subtitle --margin-top">{{ $t('services.services.title') }}</h3>
      <service-wrapper v-for="(service, index) in servicesNxt"
                       :service="service"
                       :key="index"
                       class="services__service --margin-top">
        <template #body>
          <div v-if="service.step">
            <input-number-action
                  @input="checkIsValid($event, service)"
                  @update:enabled="chooseService($event, service)"
                  v-model="service.value_chosen"
                  v-model:enabled="service.enabled"
                  :label="service.label"
                  :min="service.min || 0"
                  :max="service.max || Infinity"
                  :step="service.step">
            </input-number-action>
          </div>
          <div v-else>
            <boolean-action
                  :options="[{name: 'Non', value: false}, {name: 'Oui', value: true}]"
                  v-model="service.is_chosen"
                  :label="service.label">
            </boolean-action>
          </div>
        </template>
        <template v-if="!isValid && service.enabled" #message-error>
          <generate-error :value="service.value_chosen"
                          :max="service.max"
                          :min="service.min"></generate-error>
        </template>
        <template #options>
          <transition name="fade" mode="in-out">
            <service-options
                  class="--margin-top"
                  v-if="service.is_chosen && service.options?.length"
                  :options="service.options"
                  :service-id="service.id" />
          </transition>
        </template>
        <template #footer v-if="service.footer">
          <div v-html="service.footer"></div>
        </template>
      </service-wrapper>
    </template>
    <total :price="price" class="services__total --margin-top" :offer="offer"
           :warningPrice="!!warningVisible"></total>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { mapState, mapActions } from 'pinia';
import { useConfigStore, useServiceStore } from '@/stores';
import handShaking from '@/assets/img/hand-shaking.png';
import BooleanAction from '@/utils/components/BooleanAction.vue';
import CardFlex from '@/utils/components/CardFlex.vue';
import GenerateError from '@/components/services/serviceFragment/GenerateError.vue';
import InputNumberAction from '@/components/services/serviceFragment/InputNumberAction.vue';
import { useMoveManagerEmits, useMoveManager, useMoveManagerProps } from '@/composables/useMoveManager';
import OffersHeader from '@/components/comparator/offers/OffersHeader.vue';
import offersHelper from '@/utils/helpers/offers';
import OfferWrapper from '@/components/offer/Offer.vue';
import ServiceOptions from '@/components/services/serviceFragment/ServiceOptions.vue';
import ServiceWrapper from '@/components/services/ServiceWrapper.vue';
import Total from '@/utils/components/Total.vue';

export default {
  name: 'StepServices',
  nameClassStyle: 'services',
  emits: useMoveManagerEmits,
  props: useMoveManagerProps,
  setup(props, { emit }) {
    const servicesNxt = ref([]);
    const context = useMoveManager(props, { emit });
    context.price = computed(() => {
      if (servicesNxt.value?.length && props.offer) {
        return offersHelper.getPrice(props.offer, context.exclTax.value) + context.servicesPrice.value;
      }
      return 0;
    });
    return {
      ...context,
      servicesNxt,
    };
  },
  components: {
    BooleanAction,
    CardFlex,
    GenerateError,
    InputNumberAction,
    OffersHeader,
    OfferWrapper,
    ServiceOptions,
    ServiceWrapper,
    Total,
  },
  data() {
    return {
      hasErrorChooseServices: false,
      isValid: true,
    };
  },
  computed: {
    ...mapState(useConfigStore, ['comparatorConfig']),
    ...mapState(useServiceStore, ['servicesLoading']),
    handShaking() {
      return handShaking;
    },
  },
  created() {
    if (!this.isInitialStatus) {
      this.$router.push({ name: 'comparator' });
    } else {
      if (!this.servicesStore?.length) {
        this.$router.push({
          name: 'booking',
          params: {
            id: this.$route.params.id,
          },
          hash: '#resume',
        });
      }
      this.servicesNxt = this.servicesStore;
    }
  },
  methods: {
    ...mapActions(useConfigStore, ['setLoading']),
    ...mapActions(useServiceStore, ['setServices', 'setServicesPrice']),
    checkChooseServices() {
      this.isValid = true;
      for (const serv of this.servicesNxt) {
        const value = serv.value_chosen;
        if (serv.enabled && (!value
          || (serv.min && value < serv.min)
          || (serv.max && value > serv.max))) {
          this.isValid = false;
          return;
        }
      }
    },
    checkIsValid(value, service) {
      if (!this.isValid && service.enabled && value >= service.min && value <= service.max) {
        this.isValid = true;
      }
    },
    chooseService(value, service) {
      if (!value && service.value_chosen !== undefined) {
        delete service.value_chosen;
      }
      service.is_chosen = value;
    },
    async validate() {
      this.setLoading(true);
      try {
        this.checkChooseServices();
        if (this.isValid) {
          await this.$router.push({
            name: 'booking',
            params: {
              id: this.$route.params.id,
            },
            hash: `#${this.comparatorConfig.services.next.name}`,
          });
        }
      } finally {
        this.setLoading(false);
      }
    },
  },
  watch: {
    servicesLoading(value) {
      if (!value) {
        this.servicesNxt = this.servicesStore;
      }
    },
    servicesNxt: {
      deep: true,
      handler(value) {
        this.setServices(this.servicesNxt);
        this.setServicesPrice({ services: value, exclTax: this.exclTax });
      },
    },
  },
};
</script>

<style scoped lang="scss">
.--bold {
  font-weight: 700;
}
.--margin-bottom {
  margin-bottom: 20px;
}
.--margin-top {
  margin-top: 20px;
}
.services {
  border-radius: 0;
  @include breakpoint(tablet) {
    border-radius: 4px;
  }

  &__message-alert {
    background: var(--nxt-color-panel);
  }
  &__mover {
    font-weight: lighter;
  }
  &__subtitle {
    font-weight: lighter;
  }
  &__total {
    font-size: 23px;
    :deep(.total__warning), :deep(span) {
      font-size: 18px;
    }
  }
}
</style>
